import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckboxField } from '@brighte/form-components';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { requiredFieldError } from '@brighte/validation';
import { Loader } from '@components/Loader/Loader';
import StepFooter from '@components/StepFooter/StepFooter';
import { useAcceptApplicationContractMutation } from '@graphql/index';
import { useQuoteByTrackingId } from '@hooks';
import OfferAccepted from '@pages/ApplicationStatus/OfferAccepted';
import { useApplicationData } from '@provider';
import { Button } from '@spark-web/button';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { getDisclaimers } from '@steps/FinancePurchase/utils';
import { OFFER_ACCEPTANCE, getApplicantTypeAndTrackingIdFromUrl } from '@utils';
import LoanContract from './LoanContract';

function OfferAcceptance() {
  const { isLoading } = useQuoteByTrackingId({ enableApplication: false, enabled: true });
  const { control, handleSubmit, trigger, resetField } = useForm({
    defaultValues: {
      acceptLoanContract: false,
    },
  });
  const { user } = useAuth0();

  const { quote, application } = useApplicationData(['application', 'quote']);
  const footerItems = getDisclaimers({ quote, applicationSubStatus: application?.subStatus });
  const [isReviewed, setIsReviewed] = useState(false);
  const {
    mutate: acceptContract,
    isLoading: acceptLoading,
    data,
  } = useAcceptApplicationContractMutation({
    useErrorBoundary: true,
  });
  const { applicantType, trackingId } = getApplicantTypeAndTrackingIdFromUrl();

  const onSubmit = () => {
    acceptContract({
      acceptApplicationContractInput: {
        applicationId: trackingId,
        applicantType: applicantType,
      },
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  if (data) {
    return <OfferAccepted vendor={quote?.vendor.tradingName ?? ''} />;
  }

  return (
    <Stack padding="large" gap="xxlarge">
      <Heading level="1">
        {user?.given_name}, {OFFER_ACCEPTANCE.TITLE}
      </Heading>
      <Text size="standard">{OFFER_ACCEPTANCE.DESCRIPTION}</Text>
      <LoanContract setReview={setIsReviewed} />
      <Text tone="muted" size="small" align="center">
        {OFFER_ACCEPTANCE.CONTRACT_COPY}
      </Text>
      <CheckboxField
        control={control}
        rules={{
          required: isReviewed ? requiredFieldError() : OFFER_ACCEPTANCE.CONTRACT_REVIEW_ERROR,
          validate: {
            isContractReviewed: () => isReviewed || OFFER_ACCEPTANCE.CONTRACT_REVIEW_ERROR,
          },
        }}
        onChange={() => {
          trigger('acceptLoanContract');
          if (!isReviewed) {
            resetField('acceptLoanContract', {
              keepError: true,
            });
          }
        }}
        name="acceptLoanContract"
        label={OFFER_ACCEPTANCE.LOAN_CONFIRMATION}
      />
      <Button onClick={handleSubmit(onSubmit)} disabled={acceptLoading} loading={acceptLoading}>
        {OFFER_ACCEPTANCE.ACCEPT}
      </Button>
      <StepFooter items={footerItems} />
    </Stack>
  );
}

export default OfferAcceptance;
